import { useState, useEffect } from "react";
import { localCache } from "@/lib/cache";
import { useDistrictGuard } from "@/hooks/useDistrictGuard";
import { getChurch } from "@/lib/api";

/**
 * Returns the coordinator's church name using a three-source cascade:
 * 1. localCache 'church_${id}'       — write-through entry set by getChurches
 * 2. localCache 'churches_${distId}' — full list set by getChurches
 * 3. getChurch(id)                   — direct API call as last resort
 *
 * A 300ms retry covers the race where RequireDistrict's getChurches() is
 * still writing to cache when this hook first runs.
 */
export function useCoordinatorChurchName() {
  const { coordinatorChurchId, districtId } = useDistrictGuard();
  const [name, setName] = useState(null);

  useEffect(() => {
    if (!coordinatorChurchId || !districtId) return;
    let cancelled = false;

    async function resolve() {
      // Source 1 — individual write-through entry (fastest)
      const individual = await localCache.get(`church_${coordinatorChurchId}`).catch(() => null);
      if (!cancelled && individual?.name) { setName(individual.name); return; }

      // Source 2 — full church list
      const list = await localCache.get(`churches_${districtId}`).catch(() => null);
      if (!cancelled && Array.isArray(list)) {
        const ch = list.find((c) => c.id === coordinatorChurchId);
        if (ch?.name) { setName(ch.name); return; }
      }

      // Retry after 300ms — covers RequireDistrict's getChurches() still writing
      await new Promise((r) => setTimeout(r, 300));
      if (cancelled) return;

      // Source 1 retry
      const individual2 = await localCache.get(`church_${coordinatorChurchId}`).catch(() => null);
      if (!cancelled && individual2?.name) { setName(individual2.name); return; }

      // Source 3 — direct API call as last resort
      try {
        const church = await getChurch(coordinatorChurchId);
        if (!cancelled && church?.name) setName(church.name);
      } catch { /* ignore */ }
    }

    resolve();
    return () => { cancelled = true; };
  }, [coordinatorChurchId, districtId]);

  return name;
}
